/**
 * Webhook Testing Tool
 *
 * Admin-only page for testing SKC webhook flows through the real gateway:
 * - Tab 1: Menu Sync — Load real SKC payloads, send via gateway (POST /v2/eats/stores/{store_id}/menus)
 * - Tab 2: Item Update — Build item payloads, send via gateway (PUT /v2/eats/stores/{store_id}/menu-items/{item_id})
 *
 * All requests go through the full pipeline: Admin → Backend proxy → SKC Gateway → Lambda → Backend
 *
 * Only available in staging/development environments.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Button,
  TextField,
  Select,
  MenuItem as MuiMenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Slider,
  Switch,
  FormControlLabel,
  alpha,
  Collapse,
  Tab,
  Tabs,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  RemoveShoppingCart as OutOfStockIcon,
  Inventory as StockIcon,
  Science as TestIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  VpnKey as TokenIcon,
  Restaurant as MenuIcon,
  AltRoute as GatewayIcon,
  UploadFile as UploadIcon,
  Folder as FolderIcon,
  Casino as RandomIcon,
  Add as AddIcon,
  Edit as EditIcon,
  DeleteSweep as ClearAllIcon,
  Replay as ReplayIcon,
  Delete as DeleteIcon,
  Storage as StorageIcon,
  Warning as WarningIcon,
  CheckCircleOutline as ProcessedIcon,
  HourglassEmpty as PendingIcon,
  ExpandMore as AccordionExpandIcon,
} from '@mui/icons-material';
import { Title, useNotify } from 'react-admin';
import { usePermissions } from '../context/PermissionContext';
import { useAdminTheme } from '../admin-theme';
import config from '../config';
import Logger from '../utils/Logger';
import { getValidToken } from '../services/tokenRefreshService';

// ============================================================================
// Types
// ============================================================================

interface Store {
  id: string;
  name: string;
  externalId: string;
  legacyLocationId: string;
  address?: { city: string; suburb: string };
}

interface Product {
  id: string;
  sku: string;
  name: string;
  priceTier1: number;
  status: string;
  attachments?: { url: string }[];
  categoryRelation?: { id: string; name: string };
}

interface MenuSyncResult {
  success: boolean;
  route: 'gateway';
  timestamp: number;
  response: any;
  payloadSummary: { items: number; categories: number; menus: number; sizeKB: number };
}

interface ItemUpdateResult {
  success: boolean;
  timestamp: number;
  response: any;
  itemId: string;
  storeId: string;
  stock: number;
  suspended: boolean;
}

interface StoredPayload {
  id: string;
  eventType: string;
  itemCount: number;
  categoryCount: number;
  sizeKB: number;
  status: 'stored' | 'processing' | 'processed' | 'failed';
  errorMessage: string | null;
  createdAt: string;
  updatedAt: string;
}

interface PayloadStoreGroup {
  externalStoreId: string;
  storeName: string | null;
  storeId: string | null;
  payloads: StoredPayload[];
}

interface LoadedPayload {
  raw: any;
  filename: string;
  sizeKB: number;
  itemCount: number;
  categoryCount: number;
  menuCount: number;
  modifierGroupCount: number;
  externalStoreId: string;
  eventType: string;
  timestamp?: string;
  source?: string;
}

// ============================================================================
// Helpers
// ============================================================================

async function getAuthHeaders() {
  const token = await getValidToken();
  return { Authorization: `Bearer ${token}` };
}

/**
 * Extract display text from SKC/Uber translation object.
 * Format: { translations: { en_us: "text" } } or plain string.
 */
function extractText(field: any): string {
  if (!field) return '';
  if (typeof field === 'string') return field;
  const t = field?.translations;
  if (!t) return '';
  return t?.en_us ?? t?.en ?? Object.values(t)?.[0] ?? '';
}

/**
 * Extract stock level from selling_info.selling_options[0].quantity_constraints.max_permitted
 */
function extractStock(item: any): number {
  return item?.selling_info?.selling_options?.[0]?.quantity_constraints?.max_permitted ?? 0;
}

/**
 * Check if item is suspended (OOS).
 * Uber format: { suspension: null } = available, { suspension: { reason: "Out of stock" } } = OOS.
 * A suspension object with only suspend_until: null and no reason is NOT suspended.
 */
function isSuspended(item: any): boolean {
  const suspension = item?.suspension_info?.suspension;
  return !!suspension?.reason;
}

/**
 * Extract price in rands from cents
 */
function extractPriceDisplay(item: any): string {
  const price = item?.price_info?.overridden_price ?? item?.price_info?.price;
  if (price == null) return '-';
  return `R${(price / 100).toFixed(2)}`;
}

/**
 * Format a date string to relative time (e.g., "5m ago", "2h ago")
 */
function formatTimeAgo(dateStr: string): string {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

// ============================================================================
// Test Payload Generator
// ============================================================================

// Helper to build an Uber-format item compactly
function _item(id: string, name: string, price: number, gtin: string, stock: number, img: string, desc: string, extData: string, suspended = false) {
  return {
    id, title: { translations: { en: name, en_us: name } },
    tax_info: { tax_rate: 0, vat_rate_percentage: 15 },
    dish_info: { classifications: { alcoholic_items: 0, can_serve_alone: true } },
    image_url: `https://mngr.smartkitchenco.com/storage/products/${img}`,
    price_info: { price, priced_by_unit: { measurement_type: 'MEASUREMENT_TYPE_COUNT' } },
    description: { translations: { en_us: desc } },
    product_info: { gtin },
    selling_info: { selling_options: [{ sold_by_unit: { measurement_type: 'MEASUREMENT_TYPE_COUNT' }, quantity_constraints: { increment: 1, max_permitted: stock, min_permitted: 1, default_quantity: 1 } }] },
    external_data: extData,
    suspension_info: { suspension: suspended ? { suspend_until: null, reason: 'Out of stock' } : { suspend_until: null } },
  };
}

const BASE_PAYLOAD = {
  items: [
    // Disposable Vapes (4)
    _item('SKC000001', 'AirsPops Freezy Grape 5%', 11999, '6971895980012', 45, 'airspops_grape.jpg', 'AirsPops disposable vape with refreshing grape flavor, 5% nicotine strength', 'airs_pops_freezy_grape_5'),
    _item('SKC000002', 'Elf Bar Watermelon Ice 5%', 13999, '6971895980029', 60, 'elfbar_watermelon.jpg', 'Popular Elf Bar disposable with sweet watermelon and icy menthol', 'elf_bar_watermelon_ice_5'),
    _item('SKC000003', 'Elf Bar Blueberry Sour 5%', 13999, '6971895980067', 38, 'elfbar_blueberry.jpg', 'Elf Bar disposable with tangy blueberry sour candy flavor', 'elf_bar_blueberry_sour_5'),
    _item('SKC000004', 'Lost Mary Mango Ice 5%', 14999, '6971895980074', 22, 'lostmary_mango.jpg', 'Lost Mary disposable vape with tropical mango and cool menthol finish', 'lost_mary_mango_ice_5'),
    // Pod Devices & Kits (3)
    _item('SKC000005', 'Vaporesso XROS 3 Pod Kit', 45999, '6971895980036', 12, 'vaporesso_xros3.jpg', 'Advanced refillable pod system with adjustable airflow and 1000mAh battery', 'vaporesso_xros_3_pod_kit'),
    _item('SKC000006', 'SMOK Nord 5 Kit', 52999, '6971895980081', 8, 'smok_nord5.jpg', 'Compact pod mod kit with 2000mAh battery and RPM 3 coil compatibility', 'smok_nord_5_kit'),
    _item('SKC000007', 'Voopoo Drag X2 Kit', 64999, '6971895980098', 5, 'voopoo_dragx2.jpg', 'High-performance pod mod with 80W output and PnP coil platform', 'voopoo_drag_x2_kit'),
    // Accessories (4)
    _item('SKC000008', 'Replacement Coils 0.5\u03a9 (5-Pack)', 18999, '6971895980043', 25, 'coil_pack_05.jpg', 'Pack of 5 replacement coils, 0.5\u03a9 resistance for sub-ohm vaping', 'coil_pack_05_ohm_5_pack'),
    _item('SKC000009', 'Replacement Coils 0.8\u03a9 (5-Pack)', 17999, '6971895980105', 30, 'coil_pack_08.jpg', 'Pack of 5 replacement coils, 0.8\u03a9 resistance for MTL vaping', 'coil_pack_08_ohm_5_pack'),
    _item('SKC000010', 'USB-C Fast Charger Cable 1m', 7999, '6971895980112', 50, 'usbc_cable.jpg', 'Durable braided USB-C cable for fast charging vape devices', 'usbc_fast_charger_1m'),
    _item('SKC000011', 'Silicone Protective Case (XROS)', 5999, '6971895980129', 18, 'case_xros.jpg', 'Soft silicone case for Vaporesso XROS series with lanyard hole', 'silicone_case_xros'),
    // E-Liquids (5)
    _item('SKC000012', 'Strawberry E-Liquid 60ml 3mg', 24999, '6971895980050', 35, 'ejuice_strawberry.jpg', 'Premium strawberry flavored vape juice, 60ml bottle, 3mg nicotine strength', 'ejuice_strawberry_60ml_3mg'),
    _item('SKC000013', 'Mint Menthol E-Liquid 60ml 6mg', 24999, '6971895980136', 42, 'ejuice_mint.jpg', 'Cool menthol e-liquid with crisp peppermint notes, 60ml, 6mg nicotine', 'ejuice_mint_60ml_6mg'),
    _item('SKC000014', 'Vanilla Custard E-Liquid 30ml 12mg', 16999, '6971895980143', 15, 'ejuice_vanilla.jpg', 'Rich vanilla custard dessert flavor, 30ml bottle, 12mg nicotine', 'ejuice_vanilla_30ml_12mg'),
    _item('SKC000015', 'Mango Tango E-Liquid 60ml 3mg', 24999, '6971895980150', 28, 'ejuice_mango.jpg', 'Tropical mango blend with a tangy twist, 60ml, 3mg nicotine', 'ejuice_mango_60ml_3mg'),
    _item('SKC000016', 'Tobacco Classic E-Liquid 30ml 18mg', 14999, '6971895980167', 20, 'ejuice_tobacco.jpg', 'Traditional tobacco flavor for ex-smokers, 30ml, 18mg nicotine', 'ejuice_tobacco_30ml_18mg'),
    // Nicotine Pouches (4)
    _item('SKC000017', 'ZYN Cool Mint 6mg (20-Pack)', 8999, '6971895980174', 55, 'zyn_mint.jpg', 'Tobacco-free nicotine pouches with refreshing cool mint, 6mg per pouch', 'zyn_cool_mint_6mg'),
    _item('SKC000018', 'ZYN Citrus 3mg (20-Pack)', 8999, '6971895980181', 40, 'zyn_citrus.jpg', 'Light citrus flavored nicotine pouches, 3mg strength, tobacco-free', 'zyn_citrus_3mg'),
    _item('SKC000019', 'VELO Berry Frost 10mg (20-Pack)', 9999, '6971895980198', 33, 'velo_berry.jpg', 'Strong berry and menthol nicotine pouches, 10mg per pouch', 'velo_berry_frost_10mg'),
    _item('SKC000020', 'ON! Coffee 4mg (20-Pack)', 7999, '6971895980204', 48, 'on_coffee.jpg', 'Coffee flavored nicotine pouches with a smooth roast finish, 4mg', 'on_coffee_4mg'),
  ],
  menus: [{ id: 'menu_main', title: { translations: { en: 'Main Menu', en_us: 'Main Menu' } }, category_ids: ['cat_disposables', 'cat_pods', 'cat_accessories', 'cat_eliquids', 'cat_pouches'] }],
  categories: [
    { id: 'cat_disposables', title: { translations: { en: 'Disposable Vapes', en_us: 'Disposable Vapes' } }, entities: [{ id: 'SKC000001', type: 'ITEM' }, { id: 'SKC000002', type: 'ITEM' }, { id: 'SKC000003', type: 'ITEM' }, { id: 'SKC000004', type: 'ITEM' }] },
    { id: 'cat_pods', title: { translations: { en: 'Pod Devices & Kits', en_us: 'Pod Devices & Kits' } }, entities: [{ id: 'SKC000005', type: 'ITEM' }, { id: 'SKC000006', type: 'ITEM' }, { id: 'SKC000007', type: 'ITEM' }] },
    { id: 'cat_accessories', title: { translations: { en: 'Accessories', en_us: 'Accessories' } }, entities: [{ id: 'SKC000008', type: 'ITEM' }, { id: 'SKC000009', type: 'ITEM' }, { id: 'SKC000010', type: 'ITEM' }, { id: 'SKC000011', type: 'ITEM' }] },
    { id: 'cat_eliquids', title: { translations: { en: 'E-Liquids', en_us: 'E-Liquids' } }, entities: [{ id: 'SKC000012', type: 'ITEM' }, { id: 'SKC000013', type: 'ITEM' }, { id: 'SKC000014', type: 'ITEM' }, { id: 'SKC000015', type: 'ITEM' }, { id: 'SKC000016', type: 'ITEM' }] },
    { id: 'cat_pouches', title: { translations: { en: 'Nicotine Pouches', en_us: 'Nicotine Pouches' } }, entities: [{ id: 'SKC000017', type: 'ITEM' }, { id: 'SKC000018', type: 'ITEM' }, { id: 'SKC000019', type: 'ITEM' }, { id: 'SKC000020', type: 'ITEM' }] },
  ],
  event_type: 'menu_sync',
  external_store_id: 'test-generated',
};

const TEST_DESCRIPTIONS = [
  'TEST — Auto-generated description for pipeline validation',
  'TEST — Quality assurance product for staging environment',
  'TEST — Synthetic product to verify menu processing engine',
  'TEST — Webhook integration test entry',
];

const TEST_IMAGES = [
  'https://mngr.smartkitchenco.com/storage/products/test_placeholder_1.jpg',
  'https://mngr.smartkitchenco.com/storage/products/test_placeholder_2.jpg',
  'https://mngr.smartkitchenco.com/storage/products/test_placeholder_3.jpg',
];

const TEST_ITEM_NAMES = [
  'Widget Alpha', 'Gadget Beta', 'Doohickey Gamma', 'Thingamajig Delta',
  'Contraption Epsilon', 'Gizmo Zeta', 'Apparatus Eta', 'Device Theta',
];

function rand(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function pick<T>(arr: T[]): T {
  return arr[rand(0, arr.length - 1)];
}

function shuffleIndices(length: number): number[] {
  const a = Array.from({ length }, (_, i) => i);
  for (let i = a.length - 1; i > 0; i--) {
    const j = rand(0, i);
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

interface GeneratorOpts {
  newItems: number;
  mutateNames: number;
  mutateDescs: number;
  mutateImages: number;
  suspendCount: number;
  stockMin: number;
  stockMax: number;
  storeId: string;
  basePayload?: any;
}

function generateTestPayload(opts: GeneratorOpts): { payload: any; summary: string } {
  const base = opts.basePayload || BASE_PAYLOAD;
  const payload = structuredClone(base);

  if (opts.storeId) payload.external_store_id = opts.storeId;
  payload.event_type = 'menu_sync';

  const total = payload.items.length;
  const shuffled = shuffleIndices(total);
  let cursor = 0;

  // Collect which indices get which mutations
  const nameIndices = new Set(shuffled.slice(cursor, cursor + Math.min(opts.mutateNames, total)));
  cursor += opts.mutateNames;
  const descIndices = new Set(shuffled.slice(cursor, cursor + Math.min(opts.mutateDescs, total)));
  cursor += opts.mutateDescs;
  const imageIndices = new Set(shuffled.slice(cursor, cursor + Math.min(opts.mutateImages, total)));
  cursor += opts.mutateImages;
  const suspendIndices = new Set(shuffled.slice(cursor, cursor + Math.min(opts.suspendCount, total)));

  // Apply mutations
  for (let i = 0; i < total; i++) {
    const item = payload.items[i];

    // Randomize stock on ALL items
    if (item.selling_info?.selling_options?.[0]?.quantity_constraints) {
      item.selling_info.selling_options[0].quantity_constraints.max_permitted = rand(opts.stockMin, opts.stockMax);
    }

    // Mutate name — prepend TEST
    if (nameIndices.has(i) && item.title?.translations) {
      for (const lang of Object.keys(item.title.translations)) {
        item.title.translations[lang] = `TEST ${item.title.translations[lang]}`;
      }
    }

    // Mutate description
    if (descIndices.has(i) && item.description?.translations) {
      const desc = pick(TEST_DESCRIPTIONS);
      for (const lang of Object.keys(item.description.translations)) {
        item.description.translations[lang] = desc;
      }
    }

    // Mutate image
    if (imageIndices.has(i)) {
      item.image_url = pick(TEST_IMAGES);
    }

    // Suspend
    if (suspendIndices.has(i)) {
      item.suspension_info = { suspension: { suspend_until: null, reason: 'Out of stock' } };
      if (item.selling_info?.selling_options?.[0]?.quantity_constraints) {
        item.selling_info.selling_options[0].quantity_constraints.max_permitted = 1;
      }
    }
  }

  // Generate new test items
  const newItems: any[] = [];
  for (let n = 1; n <= opts.newItems; n++) {
    const sku = `TEST-${String(n).padStart(4, '0')}`;
    const name = `TEST ${pick(TEST_ITEM_NAMES)} #${n}`;
    newItems.push({
      id: sku,
      title: { translations: { en: name, en_us: name } },
      tax_info: { tax_rate: 0, vat_rate_percentage: 15 },
      dish_info: { classifications: { alcoholic_items: 0, can_serve_alone: true } },
      image_url: pick(TEST_IMAGES),
      price_info: { price: rand(999, 99999), priced_by_unit: { measurement_type: 'MEASUREMENT_TYPE_COUNT' } },
      description: { translations: { en_us: pick(TEST_DESCRIPTIONS) } },
      product_info: { gtin: `TEST${rand(1000000000, 9999999999)}` },
      selling_info: { selling_options: [{ sold_by_unit: { measurement_type: 'MEASUREMENT_TYPE_COUNT' }, quantity_constraints: { increment: 1, max_permitted: rand(5, 80), min_permitted: 1, default_quantity: 1 } }] },
      external_data: sku.toLowerCase().replace(/-/g, '_'),
      suspension_info: { suspension: { suspend_until: null } },
    });
  }

  payload.items.push(...newItems);

  // Add new items to a test category
  if (newItems.length > 0) {
    payload.categories.push({
      id: 'cat_test_items',
      title: { translations: { en: 'TEST New Items', en_us: 'TEST New Items' } },
      entities: newItems.map((i: any) => ({ id: i.id, type: 'ITEM' })),
    });
    if (payload.menus?.[0]?.category_ids) {
      payload.menus[0].category_ids.push('cat_test_items');
    }
  }

  const parts: string[] = [];
  parts.push(`${payload.items.length} items (${total} base + ${opts.newItems} new)`);
  parts.push(`stock ${opts.stockMin}-${opts.stockMax}`);
  if (nameIndices.size > 0) parts.push(`${nameIndices.size} names changed`);
  if (descIndices.size > 0) parts.push(`${descIndices.size} descriptions changed`);
  if (imageIndices.size > 0) parts.push(`${imageIndices.size} images changed`);
  if (suspendIndices.size > 0) parts.push(`${suspendIndices.size} suspended`);

  return { payload, summary: parts.join(', ') };
}

// ============================================================================
// Tab Panel helper
// ============================================================================

function TabPanel({ children, value, index }: { children: React.ReactNode; value: number; index: number }) {
  return value === index ? <Box>{children}</Box> : null;
}

// ============================================================================
// Component
// ============================================================================

export default function WebhookTestingTool() {
  const { colors, glassHeaderStyle, elevatedInputStyle, cardStyle } = useAdminTheme();
  const { role, loading: permissionsLoading } = usePermissions();
  const notify = useNotify();

  // Shared state
  const [stores, setStores] = useState<Store[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState({ stores: false, products: false, sending: false, token: false, menuSend: false, loadDefault: false });
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(0);

  // Menu sync state
  const [loadedPayload, setLoadedPayload] = useState<LoadedPayload | null>(null);
  const [menuPayload, setMenuPayload] = useState<any>(null);
  const [menuResults, setMenuResults] = useState<MenuSyncResult[]>([]);
  const [showMenuResults, setShowMenuResults] = useState(true);
  const [storeIdOverride, setStoreIdOverride] = useState('');
  const [showItemPreview, setShowItemPreview] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showGatewayPayload, setShowGatewayPayload] = useState(false);
  const [originalPayload, setOriginalPayload] = useState<any>(null); // Tracks the unmodified base (file upload or default)

  // Test payload generator state
  const [showGenerator, setShowGenerator] = useState(false);
  const [genNewItems, setGenNewItems] = useState(2);
  const [genMutateNames, setGenMutateNames] = useState(2);
  const [genMutateDescs, setGenMutateDescs] = useState(1);
  const [genMutateImages, setGenMutateImages] = useState(1);
  const [genSuspendCount, setGenSuspendCount] = useState(1);
  const [genStockMin, setGenStockMin] = useState(1);
  const [genStockMax, setGenStockMax] = useState(100);
  const [genStoreId, setGenStoreId] = useState('');

  // Item update state
  const [itemStoreId, setItemStoreId] = useState('');
  const [itemProductSearch, setItemProductSearch] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [itemStock, setItemStock] = useState(5);
  const [itemSuspended, setItemSuspended] = useState(false);
  const [itemResults, setItemResults] = useState<ItemUpdateResult[]>([]);
  const [showItemResults, setShowItemResults] = useState(true);
  const [itemIdOverride, setItemIdOverride] = useState('');

  // Staging reset state
  const [resetting, setResetting] = useState(false);

  // Payloads tab state
  const [payloadStores, setPayloadStores] = useState<PayloadStoreGroup[]>([]);
  const [payloadTotal, setPayloadTotal] = useState(0);
  const [payloadStatusFilter, setPayloadStatusFilter] = useState<string>('');
  const [payloadLoading, setPayloadLoading] = useState(false);
  const [rerunningId, setRerunningId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // SKC Token state

  const isProduction = useMemo(() => {
    const host = document.location.host;
    return !host.includes('localhost') && !host.includes('staging') && !host.includes('dopeassdevs');
  }, []);

  // ---- Fetch stores ----
  const fetchStores = useCallback(async () => {
    setLoading((l) => ({ ...l, stores: true }));
    try {
      const resp = await fetch(`${config.API_URL}/admin/picker/stores`, {
        headers: await getAuthHeaders(),
      });
      if (!resp.ok) throw new Error(`Failed to fetch stores: ${resp.status}`);
      const data = await resp.json();
      const storeList = data.stores || data.data || data;
      setStores(Array.isArray(storeList) ? storeList : []);
    } catch (err: any) {
      Logger.error('[WebhookTest] Failed to fetch stores:', err);
      setError(`Failed to load stores: ${err.message}`);
    } finally {
      setLoading((l) => ({ ...l, stores: false }));
    }
  }, []);

  // ---- Fetch products ----
  const fetchProducts = useCallback(
    async (search?: string) => {
      setLoading((l) => ({ ...l, products: true }));
      try {
        const params = new URLSearchParams({ limit: '100' });
        if (search) params.set('search', search);
        const resp = await fetch(`${config.API_URL}/admin/picker/products?${params}`, {
          headers: await getAuthHeaders(),
        });
        if (!resp.ok) throw new Error(`Failed to fetch products: ${resp.status}`);
        const data = await resp.json();
        const productList = data.data?.products || data.products || data.data || data;
        setProducts(Array.isArray(productList) ? productList : []);
      } catch (err: any) {
        Logger.error('[WebhookTest] Failed to fetch products:', err);
        setError(`Failed to load products: ${err.message}`);
      } finally {
        setLoading((l) => ({ ...l, products: false }));
      }
    },
    [],
  );

  // ---- Initial load ----
  useEffect(() => {
    if (!isProduction) {
      fetchStores();
      fetchProducts();
    }
  }, [fetchStores, fetchProducts, isProduction]);

  // ---- Payloads tab: Fetch stored payloads ----
  const fetchPayloads = useCallback(async (statusFilter?: string) => {
    setPayloadLoading(true);
    try {
      const params = new URLSearchParams({ limit: '200' });
      if (statusFilter) params.set('status', statusFilter);
      const resp = await fetch(`${config.API_URL}/vaperite/staging/payloads?${params}`, {
        headers: await getAuthHeaders(),
      });
      const data = await resp.json();
      if (data.success) {
        setPayloadStores(data.stores || []);
        setPayloadTotal(data.total || 0);
      }
    } catch (err: any) {
      notify(`Failed to load payloads: ${err.message}`, { type: 'error' });
    } finally {
      setPayloadLoading(false);
    }
  }, [notify]);

  // Auto-fetch payloads when switching to Payloads tab
  useEffect(() => {
    if (activeTab === 2) fetchPayloads(payloadStatusFilter || undefined);
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRerunPayload = useCallback(async (payloadId: string) => {
    setRerunningId(payloadId);
    try {
      const resp = await fetch(`${config.API_URL}/vaperite/staging/payloads/${payloadId}/rerun`, {
        method: 'POST',
        headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
      });
      const data = await resp.json();
      if (data.success) {
        notify(`Payload reprocessed: ${data.processing?.processedImmediately || 0} immediate, ${data.processing?.stagedForApproval || 0} staged`, { type: 'success' });
      } else {
        notify(`Rerun failed: ${data.error}`, { type: 'error' });
      }
      // Refresh the list
      fetchPayloads(payloadStatusFilter || undefined);
    } catch (err: any) {
      notify(`Rerun error: ${err.message}`, { type: 'error' });
    } finally {
      setRerunningId(null);
    }
  }, [notify, fetchPayloads, payloadStatusFilter]);

  const handleDeletePayload = useCallback(async (payloadId: string) => {
    setDeletingId(payloadId);
    try {
      const resp = await fetch(`${config.API_URL}/vaperite/staging/payloads/${payloadId}`, {
        method: 'DELETE',
        headers: await getAuthHeaders(),
      });
      const data = await resp.json();
      if (data.success) {
        notify('Payload deleted', { type: 'success' });
      } else {
        notify(`Delete failed: ${data.error}`, { type: 'error' });
      }
      fetchPayloads(payloadStatusFilter || undefined);
    } catch (err: any) {
      notify(`Delete error: ${err.message}`, { type: 'error' });
    } finally {
      setDeletingId(null);
    }
  }, [notify, fetchPayloads, payloadStatusFilter]);

  const handleBulkDeletePayloads = useCallback(async (status: string) => {
    if (!window.confirm(`Delete ALL ${status} payloads?`)) return;
    try {
      const resp = await fetch(`${config.API_URL}/vaperite/staging/payloads?status=${status}`, {
        method: 'DELETE',
        headers: await getAuthHeaders(),
      });
      const data = await resp.json();
      notify(`Deleted ${data.deleted || 0} ${status} payloads`, { type: 'success' });
      fetchPayloads(payloadStatusFilter || undefined);
    } catch (err: any) {
      notify(`Bulk delete error: ${err.message}`, { type: 'error' });
    }
  }, [notify, fetchPayloads, payloadStatusFilter]);

  // ---- Menu Sync: Load payload from file ----
  const handleFileUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const raw = JSON.parse(e.target?.result as string);
        const sizeKB = Math.round(file.size / 1024);

        const loaded: LoadedPayload = {
          raw,
          filename: file.name,
          sizeKB,
          itemCount: raw.items?.length || 0,
          categoryCount: raw.categories?.length || 0,
          menuCount: raw.menus?.length || 0,
          modifierGroupCount: raw.modifier_groups?.length || 0,
          externalStoreId: raw.external_store_id || '',
          eventType: raw.event_type || 'unknown',
          timestamp: raw.timestamp,
          source: raw.source,
        };

        setLoadedPayload(loaded);
        setMenuPayload(raw);
        setOriginalPayload(structuredClone(raw));
        setStoreIdOverride(raw.external_store_id || '');

        // Count stock stats
        const items = raw.items || [];
        const oosCount = items.filter((i: any) => isSuspended(i)).length;
        const withPrice = items.filter((i: any) => i.price_info?.price != null).length;

        notify(
          `Loaded ${file.name}: ${items.length} items (${oosCount} OOS), ${raw.categories?.length || 0} categories, ${sizeKB} KB`,
          { type: 'success' },
        );
      } catch (err: any) {
        Logger.error('[WebhookTest] Failed to parse JSON file:', err);
        setError(`Failed to parse file: ${err.message}`);
        notify('Invalid JSON file', { type: 'error' });
      }
    };
    reader.readAsText(file);

    // Reset input so same file can be re-uploaded
    event.target.value = '';
  }, [notify]);

  // ---- Clear staging area ----
  const handleResetStaging = useCallback(async () => {
    if (!window.confirm('Clear ALL staging data?\n\nThis will delete all staged items, field changes, and stored menu payloads. This cannot be undone.')) return;

    setResetting(true);
    try {
      const resp = await fetch(`${config.API_URL}/vaperite/staging/reset`, {
        method: 'DELETE',
        headers: await getAuthHeaders(),
      });
      const data = await resp.json();
      if (!resp.ok || !data.success) throw new Error(data.error || `Failed: ${resp.status}`);

      const total = Object.values(data.cleared as Record<string, number>).reduce((sum: number, v) => sum + (typeof v === 'number' ? v : 0), 0);
      notify(`Staging area cleared: ${total} rows deleted across ${Object.keys(data.cleared).length} tables`, { type: 'success' });
    } catch (err: any) {
      Logger.error('[WebhookTest] Staging reset failed:', err);
      notify(`Reset failed: ${err.message}`, { type: 'error' });
    } finally {
      setResetting(false);
    }
  }, [notify]);

  // ---- Menu Sync: Generate test payload ----
  const handleGenerate = useCallback(() => {
    // Always generate from the original (unmodified) base — never the previously mutated payload
    const basePayload = originalPayload || undefined;
    const { payload, summary } = generateTestPayload({
      newItems: genNewItems,
      mutateNames: genMutateNames,
      mutateDescs: genMutateDescs,
      mutateImages: genMutateImages,
      suspendCount: genSuspendCount,
      stockMin: genStockMin,
      stockMax: genStockMax,
      storeId: genStoreId || storeIdOverride || '',
      basePayload,
    });

    const json = JSON.stringify(payload);
    const sizeKB = Math.round(new Blob([json]).size / 1024);

    const loaded: LoadedPayload = {
      raw: payload,
      filename: `generated-test-${Date.now()}.json`,
      sizeKB: Math.round(sizeKB),
      itemCount: payload.items?.length || 0,
      categoryCount: payload.categories?.length || 0,
      menuCount: payload.menus?.length || 0,
      modifierGroupCount: payload.modifier_groups?.length || 0,
      externalStoreId: payload.external_store_id || '',
      eventType: payload.event_type || 'menu_sync',
    };

    setLoadedPayload(loaded);
    setMenuPayload(payload);
    setStoreIdOverride(payload.external_store_id || storeIdOverride || '');

    notify(`Generated test payload: ${summary}`, { type: 'success' });
  }, [originalPayload, genNewItems, genMutateNames, genMutateDescs, genMutateImages, genSuspendCount, genStockMin, genStockMax, genStoreId, storeIdOverride, notify]);

  // ---- Menu Sync: Build final payload (strip Lambda-injected fields) ----
  const getFinalPayload = useCallback(() => {
    if (!menuPayload) return null;
    // Strip Lambda-injected fields — the gateway Lambda will re-inject them
    const { external_store_id: _esi, event_type: _et, timestamp: _ts, source: _src, ...cleanPayload } = menuPayload;
    return cleanPayload;
  }, [menuPayload]);

  // ---- Item Update: Build payload ----
  const buildItemPayload = useCallback(() => {
    return {
      selling_info: {
        selling_options: [{
          quantity_constraints: {
            min_permitted: 1,
            max_permitted: itemStock,
            increment: 1,
            default_quantity: 1,
          },
        }],
      },
      suspension_info: {
        suspension: itemSuspended
          ? { suspend_until: 4854278400, reason: 'Out of stock' }
          : null,
      },
    };
  }, [itemStock, itemSuspended]);

  // ---- Permission check ----
  if (permissionsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress sx={{ color: colors.info }} />
      </Box>
    );
  }

  // Staff reach a page only through its tick (PageGate), so a ticked page is theirs to use
  const hasAccess = role === 'owner' || role === 'admin' || role === 'staff';
  if (!hasAccess) {
    return (
      <Alert severity="error" sx={{ m: 3, bgcolor: alpha(colors.error, 0.1), color: colors.error, border: `1px solid ${alpha(colors.error, 0.3)}` }}>
        Access Denied: This page is only accessible by owners and administrators.
      </Alert>
    );
  }

  if (isProduction) {
    return (
      <Alert severity="warning" sx={{ m: 3, bgcolor: alpha(colors.warning, 0.1), color: colors.warning, border: `1px solid ${alpha(colors.warning, 0.3)}` }}>
        Webhook testing is disabled in production environments.
      </Alert>
    );
  }

  // Compute stats from loaded payload
  const payloadStats = useMemo(() => {
    if (!menuPayload?.items) return null;
    const items = menuPayload.items as any[];
    const suspended = items.filter((i) => isSuspended(i)).length;
    const withPrice = items.filter((i) => i.price_info?.price != null).length;
    const withImage = items.filter((i) => i.image_url).length;
    const lowStock = items.filter((i) => extractStock(i) === 1 && !isSuspended(i)).length;
    return { suspended, withPrice, withImage, lowStock, total: items.length };
  }, [menuPayload]);

  return (
    <Box sx={{ bgcolor: colors.bgDeepest, minHeight: '100vh' }}>
      <Title title="Webhook Testing" />

      {/* Header */}
      <Box sx={{ ...glassHeaderStyle, px: 3, py: 2.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Typography variant="h5" sx={{ color: colors.textPrimary, fontWeight: 800, letterSpacing: '-0.02em' }}>
                Webhook Testing Tool
              </Typography>
              <Chip label="DEV" size="small" sx={{
                bgcolor: alpha(colors.warning, 0.15), color: colors.warning,
                fontWeight: 700, fontSize: '0.65rem', height: 22, border: `1px solid ${alpha(colors.warning, 0.3)}`,
              }} />
            </Stack>
            <Typography variant="body2" sx={{ color: colors.textSecondary, mt: 0.25 }}>
              Test SKC webhook flows through the real gateway — menu sync and item updates
            </Typography>
          </Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Tooltip title="Refresh stores & products">
              <IconButton
                onClick={() => { fetchStores(); fetchProducts(); }}
                sx={{ color: colors.textSecondary, '&:hover': { color: colors.info } }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Box>

      {/* Tabs */}
      <Box sx={{ px: 3, pt: 1 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{
            '& .MuiTab-root': { color: colors.textMuted, textTransform: 'none', fontWeight: 600, fontSize: '0.85rem', minHeight: 42 },
            '& .Mui-selected': { color: colors.info },
            '& .MuiTabs-indicator': { bgcolor: colors.info },
          }}
        >
          <Tab icon={<MenuIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Menu Sync" />
          <Tab icon={<StockIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Item Update" />
          <Tab icon={<StorageIcon sx={{ fontSize: 18 }} />} iconPosition="start" label={
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <span>Payloads</span>
              {payloadStores.some(s => s.payloads.some(p => p.status === 'failed')) && (
                <Chip size="small" label="!" color="error" sx={{ height: 18, minWidth: 18, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.5 } }} />
              )}
            </Stack>
          } />
        </Tabs>
      </Box>

      {/* Content */}
      <Box sx={{ px: 3, pb: 4, pt: 1 }}>
        {error && (
          <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2, bgcolor: alpha(colors.error, 0.1), color: colors.error, border: `1px solid ${alpha(colors.error, 0.3)}` }}>
            {error}
          </Alert>
        )}

        {/* ============================================================ */}
        {/* TAB 0: Menu Sync */}
        {/* ============================================================ */}
        <TabPanel value={activeTab} index={0}>
          {/* File Upload + Store Override */}
          <Paper sx={{ ...cardStyle, p: 2.5, mb: 2 }}>
            <Stack spacing={2.5}>
              {/* File upload area */}
              <Box>
                <Typography variant="subtitle2" sx={{ color: colors.textSecondary, fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1 }}>
                  Load Real SKC Payload
                </Typography>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json"
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                />
                <Stack direction="row" spacing={2} alignItems="center">
                  <Button
                    variant="outlined"
                    startIcon={<UploadIcon />}
                    onClick={() => fileInputRef.current?.click()}
                    sx={{
                      color: colors.info, borderColor: alpha(colors.info, 0.5),
                      borderStyle: 'dashed', px: 3,
                      '&:hover': { borderColor: colors.info, bgcolor: alpha(colors.info, 0.08) },
                    }}
                  >
                    Choose .json File
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={loading.loadDefault ? <CircularProgress size={14} /> : <MenuIcon />}
                    disabled={loading.loadDefault}
                    onClick={async () => {
                      setLoading((l) => ({ ...l, loadDefault: true }));
                      try {
                        const resp = await fetch(`${config.API_URL}/vaperite/staging/payloads/latest`, { headers: await getAuthHeaders() });
                        const data = await resp.json();
                        if (!resp.ok || !data.success) throw new Error(data.error || `Failed: ${resp.status}`);

                        const raw = data.payload;
                        const json = JSON.stringify(raw);
                        const sizeKB = Math.round(new Blob([json]).size / 1024);

                        setLoadedPayload({
                          raw,
                          filename: `payload-${data.externalStoreId}-${new Date(data.createdAt).toISOString().slice(0, 10)}.json`,
                          sizeKB,
                          itemCount: raw.items?.length || data.itemCount || 0,
                          categoryCount: raw.categories?.length || data.categoryCount || 0,
                          menuCount: raw.menus?.length || 0,
                          modifierGroupCount: raw.modifier_groups?.length || 0,
                          externalStoreId: raw.external_store_id || data.externalStoreId || '',
                          eventType: raw.event_type || 'menu_sync',
                        });
                        setMenuPayload(raw);
                        setOriginalPayload(structuredClone(raw));
                        setStoreIdOverride(raw.external_store_id || data.externalStoreId || '');
                        notify(`Loaded latest payload: ${raw.items?.length || 0} items, ${raw.categories?.length || 0} categories, ${sizeKB} KB`, { type: 'success' });
                      } catch (err: any) {
                        // Fall back to embedded test payload if DB is empty
                        Logger.warn('[WebhookTest] No stored payload, falling back to test payload:', err.message);
                        const raw = structuredClone(BASE_PAYLOAD);
                        const json = JSON.stringify(raw);
                        const sizeKB = Math.round(new Blob([json]).size / 1024);
                        setLoadedPayload({
                          raw,
                          filename: 'test-payload-20items.json',
                          sizeKB,
                          itemCount: raw.items.length,
                          categoryCount: raw.categories.length,
                          menuCount: raw.menus.length,
                          modifierGroupCount: 0,
                          externalStoreId: raw.external_store_id,
                          eventType: raw.event_type,
                        });
                        setMenuPayload(raw);
                        setOriginalPayload(structuredClone(raw));
                        setStoreIdOverride(raw.external_store_id);
                        notify(`No stored payloads yet — loaded test payload (${raw.items.length} items). Send it once to populate the DB, then Load Latest will fetch the real one.`, { type: 'warning' });
                      } finally {
                        setLoading((l) => ({ ...l, loadDefault: false }));
                      }
                    }}
                    sx={{
                      color: colors.purple, borderColor: alpha(colors.purple, 0.4),
                      fontSize: '0.75rem',
                      '&:hover': { borderColor: colors.purple, bgcolor: alpha(colors.purple, 0.08) },
                    }}
                  >
                    {loading.loadDefault ? 'Loading...' : 'Load Latest Payload'}
                  </Button>
                  {loadedPayload && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <FolderIcon sx={{ color: colors.textMuted, fontSize: 16 }} />
                      <Typography variant="body2" sx={{ color: colors.textPrimary, fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {loadedPayload.filename}
                      </Typography>
                      <Chip label={`${loadedPayload.sizeKB} KB`} size="small" sx={{ bgcolor: alpha(colors.textMuted, 0.12), color: colors.textMuted, fontSize: '0.6rem', height: 20 }} />
                    </Stack>
                  )}
                </Stack>
                <Typography variant="caption" sx={{ color: colors.textMuted, mt: 0.5, display: 'block' }}>
                  Upload a file or fetch the latest real payload from the database
                </Typography>
              </Box>

              <Divider sx={{ borderColor: colors.border }} />

              {/* Test Payload Generator */}
              <Box>
                <Stack
                  direction="row" alignItems="center" spacing={1} sx={{ cursor: 'pointer', mb: showGenerator ? 1.5 : 0 }}
                  onClick={() => setShowGenerator(!showGenerator)}
                >
                  <RandomIcon sx={{ color: colors.purple, fontSize: 18 }} />
                  <Typography variant="subtitle2" sx={{ color: colors.textSecondary, fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Generate Test Payload
                  </Typography>
                  <Chip label="randomized" size="small" sx={{ bgcolor: alpha(colors.purple, 0.12), color: colors.purple, fontSize: '0.6rem', height: 18 }} />
                  {originalPayload && <Chip label={`base: ${originalPayload.items?.length || 0} items`} size="small" sx={{ bgcolor: alpha(colors.info, 0.12), color: colors.info, fontSize: '0.6rem', height: 18 }} />}
                  <Box sx={{ flex: 1 }} />
                  {showGenerator ? <ExpandLessIcon sx={{ color: colors.textMuted, fontSize: 18 }} /> : <ExpandMoreIcon sx={{ color: colors.textMuted, fontSize: 18 }} />}
                </Stack>

                <Collapse in={showGenerator}>
                  <Box sx={{ p: 2, borderRadius: 1, bgcolor: alpha(colors.bgElevated, 0.5), border: `1px solid ${alpha(colors.purple, 0.15)}` }}>
                    <Stack spacing={2.5}>
                      {/* Row 1: New items + Store ID */}
                      <Stack direction="row" spacing={3} alignItems="flex-end">
                        <Box sx={{ width: 200 }}>
                          <Typography variant="caption" sx={{ color: colors.textMuted, fontWeight: 600, fontSize: '0.65rem', display: 'block', mb: 0.5 }}>
                            <AddIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                            New test items (SKU: TEST-*)
                          </Typography>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Slider value={genNewItems} onChange={(_, v) => setGenNewItems(v as number)} min={0} max={10} size="small"
                              sx={{ color: colors.purple, flex: 1, '& .MuiSlider-thumb': { width: 14, height: 14 } }} />
                            <Typography variant="body2" sx={{ color: colors.purple, fontWeight: 700, minWidth: 20, textAlign: 'center' }}>{genNewItems}</Typography>
                          </Stack>
                        </Box>
                        <TextField
                          label="Store ID override"
                          size="small"
                          value={genStoreId}
                          onChange={(e) => setGenStoreId(e.target.value)}
                          placeholder="optional — uses current store ID"
                          sx={{ minWidth: 300, ...elevatedInputStyle }}
                          InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
                        />
                      </Stack>

                      {/* Row 2: Mutation counts */}
                      <Stack direction="row" spacing={3}>
                        <Box sx={{ width: 200 }}>
                          <Typography variant="caption" sx={{ color: colors.textMuted, fontWeight: 600, fontSize: '0.65rem', display: 'block', mb: 0.5 }}>
                            <EditIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                            Change names (prepend TEST)
                          </Typography>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Slider value={genMutateNames} onChange={(_, v) => setGenMutateNames(v as number)} min={0} max={originalPayload?.items?.length || menuPayload?.items?.length || 20} size="small"
                              sx={{ color: colors.warning, flex: 1, '& .MuiSlider-thumb': { width: 14, height: 14 } }} />
                            <Typography variant="body2" sx={{ color: colors.warning, fontWeight: 700, minWidth: 20, textAlign: 'center' }}>{genMutateNames}</Typography>
                          </Stack>
                        </Box>

                        <Box sx={{ width: 200 }}>
                          <Typography variant="caption" sx={{ color: colors.textMuted, fontWeight: 600, fontSize: '0.65rem', display: 'block', mb: 0.5 }}>
                            <EditIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                            Change descriptions
                          </Typography>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Slider value={genMutateDescs} onChange={(_, v) => setGenMutateDescs(v as number)} min={0} max={originalPayload?.items?.length || menuPayload?.items?.length || 20} size="small"
                              sx={{ color: colors.warning, flex: 1, '& .MuiSlider-thumb': { width: 14, height: 14 } }} />
                            <Typography variant="body2" sx={{ color: colors.warning, fontWeight: 700, minWidth: 20, textAlign: 'center' }}>{genMutateDescs}</Typography>
                          </Stack>
                        </Box>

                        <Box sx={{ width: 200 }}>
                          <Typography variant="caption" sx={{ color: colors.textMuted, fontWeight: 600, fontSize: '0.65rem', display: 'block', mb: 0.5 }}>
                            <EditIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                            Change images
                          </Typography>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Slider value={genMutateImages} onChange={(_, v) => setGenMutateImages(v as number)} min={0} max={originalPayload?.items?.length || menuPayload?.items?.length || 20} size="small"
                              sx={{ color: colors.warning, flex: 1, '& .MuiSlider-thumb': { width: 14, height: 14 } }} />
                            <Typography variant="body2" sx={{ color: colors.warning, fontWeight: 700, minWidth: 20, textAlign: 'center' }}>{genMutateImages}</Typography>
                          </Stack>
                        </Box>
                      </Stack>

                      {/* Row 3: Suspend + Stock range */}
                      <Stack direction="row" spacing={3}>
                        <Box sx={{ width: 200 }}>
                          <Typography variant="caption" sx={{ color: colors.textMuted, fontWeight: 600, fontSize: '0.65rem', display: 'block', mb: 0.5 }}>
                            <OutOfStockIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                            Suspend items (OOS)
                          </Typography>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Slider value={genSuspendCount} onChange={(_, v) => setGenSuspendCount(v as number)} min={0} max={originalPayload?.items?.length || menuPayload?.items?.length || 20} size="small"
                              sx={{ color: colors.error, flex: 1, '& .MuiSlider-thumb': { width: 14, height: 14 } }} />
                            <Typography variant="body2" sx={{ color: colors.error, fontWeight: 700, minWidth: 20, textAlign: 'center' }}>{genSuspendCount}</Typography>
                          </Stack>
                        </Box>

                        <Box sx={{ width: 300 }}>
                          <Typography variant="caption" sx={{ color: colors.textMuted, fontWeight: 600, fontSize: '0.65rem', display: 'block', mb: 0.5 }}>
                            <StockIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                            Stock range (min – max)
                          </Typography>
                          <Stack direction="row" alignItems="center" spacing={1.5}>
                            <TextField value={genStockMin} onChange={(e) => setGenStockMin(Math.max(1, parseInt(e.target.value) || 1))} size="small" type="number"
                              inputProps={{ min: 1, max: 999, style: { width: 50, padding: '4px 8px' } }}
                              sx={{ '& .MuiInputBase-root': { bgcolor: colors.bgElevated, color: colors.textPrimary, height: 32 } }} />
                            <Typography variant="caption" sx={{ color: colors.textMuted }}>to</Typography>
                            <TextField value={genStockMax} onChange={(e) => setGenStockMax(Math.max(1, parseInt(e.target.value) || 1))} size="small" type="number"
                              inputProps={{ min: 1, max: 999, style: { width: 50, padding: '4px 8px' } }}
                              sx={{ '& .MuiInputBase-root': { bgcolor: colors.bgElevated, color: colors.textPrimary, height: 32 } }} />
                          </Stack>
                        </Box>
                      </Stack>

                      {/* Generate button */}
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Button
                          variant="contained"
                          startIcon={<RandomIcon />}
                          onClick={handleGenerate}
                          sx={{
                            bgcolor: colors.purple, fontWeight: 700,
                            '&:hover': { bgcolor: alpha(colors.purple, 0.85) },
                          }}
                        >
                          Generate Payload
                        </Button>
                        <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '0.65rem', fontStyle: 'italic' }}>
                          {originalPayload
                            ? `Generates from base (${originalPayload.items?.length || 0} items) + ${genNewItems} new`
                            : `Uses built-in base (${BASE_PAYLOAD.items.length} items) + ${genNewItems} new`
                          }
                        </Typography>
                      </Stack>
                    </Stack>
                  </Box>
                </Collapse>
              </Box>

              {/* Store ID override */}
              {loadedPayload && (
                <>
                  <Divider sx={{ borderColor: colors.border }} />
                  <Stack direction="row" spacing={2} alignItems="center">
                    <TextField
                      label="external_store_id"
                      size="small"
                      value={storeIdOverride}
                      onChange={(e) => setStoreIdOverride(e.target.value)}
                      sx={{ minWidth: 400, ...elevatedInputStyle }}
                      InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
                    />
                    <Typography variant="caption" sx={{ color: colors.warning, fontSize: '0.7rem' }}>
                      May have been injected by old Lambda — verify this maps to a real store
                    </Typography>
                  </Stack>

                  {/* Match against known stores */}
                  {stores.length > 0 && (
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '0.7rem' }}>
                        Known stores:
                      </Typography>
                      {stores.map((s) => (
                        <Chip
                          key={s.id}
                          label={`${s.name} (${s.legacyLocationId || 'no legacy id'})`}
                          size="small"
                          onClick={() => setStoreIdOverride(s.legacyLocationId || s.externalId)}
                          sx={{
                            bgcolor: storeIdOverride === s.legacyLocationId || storeIdOverride === s.externalId
                              ? alpha(colors.success, 0.15)
                              : alpha(colors.bgElevated, 0.8),
                            color: storeIdOverride === s.legacyLocationId || storeIdOverride === s.externalId
                              ? colors.success
                              : colors.textSecondary,
                            fontSize: '0.65rem', height: 24, cursor: 'pointer',
                            border: `1px solid ${storeIdOverride === s.legacyLocationId || storeIdOverride === s.externalId ? alpha(colors.success, 0.3) : 'transparent'}`,
                            '&:hover': { bgcolor: alpha(colors.info, 0.1) },
                          }}
                        />
                      ))}
                    </Stack>
                  )}
                </>
              )}

              <Divider sx={{ borderColor: colors.border }} />

              {/* Action buttons */}
              <Stack direction="row" spacing={2} alignItems="center">

                {menuPayload && (
                  <>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setShowGatewayPayload(!showGatewayPayload)}
                      sx={{ color: colors.purple, borderColor: alpha(colors.purple, 0.3), fontSize: '0.75rem', '&:hover': { borderColor: colors.purple, bgcolor: alpha(colors.purple, 0.08) } }}
                    >
                      {showGatewayPayload ? 'Hide Payload' : 'Preview Payload'}
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => { setMenuPayload(null); setLoadedPayload(null); setStoreIdOverride(''); setShowGatewayPayload(false); }}
                      sx={{ color: colors.textMuted, borderColor: alpha(colors.textMuted, 0.3), fontSize: '0.75rem' }}
                    >
                      Clear
                    </Button>
                  </>
                )}

                <Box sx={{ flex: 1 }} />

                <Button
                  variant="outlined"
                  size="small"
                  startIcon={resetting ? <CircularProgress size={14} color="inherit" /> : <ClearAllIcon />}
                  onClick={handleResetStaging}
                  disabled={resetting}
                  sx={{
                    color: colors.error, borderColor: alpha(colors.error, 0.3), fontSize: '0.75rem',
                    '&:hover': { borderColor: colors.error, bgcolor: alpha(colors.error, 0.08) },
                  }}
                >
                  {resetting ? 'Clearing...' : 'Clear Staging Area'}
                </Button>
              </Stack>

              {/* Gateway payload preview (what actually gets sent — Lambda-injected fields stripped) */}
              <Collapse in={showGatewayPayload && !!menuPayload}>
                <Box>
                  <Typography variant="caption" sx={{ color: colors.textMuted, fontWeight: 600, textTransform: 'uppercase', fontSize: '0.65rem', mb: 0.5, display: 'block' }}>
                    Gateway Payload — POST /v2/eats/stores/{storeIdOverride || '{store_id}'}/menus
                  </Typography>
                  <Typography variant="caption" sx={{ color: colors.warning, fontSize: '0.6rem', display: 'block', mb: 0.5 }}>
                    Lambda-injected fields stripped: external_store_id, event_type, timestamp, source
                  </Typography>
                  <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: alpha(colors.bgElevated, 0.5), fontFamily: 'monospace', fontSize: '0.65rem', color: colors.textSecondary, whiteSpace: 'pre-wrap', maxHeight: 300, overflow: 'auto' }}>
                    {(() => {
                      const p = getFinalPayload();
                      if (!p) return '{}';
                      // Show truncated version — first 200 lines
                      const full = JSON.stringify(p, null, 2);
                      const lines = full.split('\n');
                      if (lines.length > 200) return lines.slice(0, 200).join('\n') + `\n\n... (${lines.length - 200} more lines, ${Math.round(full.length / 1024)} KB total)`;
                      return full;
                    })()}
                  </Box>
                </Box>
              </Collapse>
            </Stack>
          </Paper>

          {/* Payload Summary */}
          {loadedPayload && menuPayload && (
            <Paper sx={{ ...cardStyle, p: 2.5, mb: 2 }}>
              <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 700, mb: 1.5 }}>
                Payload Summary
              </Typography>

              {/* Stats chips */}
              <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                <Chip label={`${loadedPayload.itemCount} items`} sx={{ bgcolor: alpha(colors.info, 0.12), color: colors.info, fontWeight: 600 }} />
                <Chip label={`${loadedPayload.categoryCount} categories`} sx={{ bgcolor: alpha(colors.purple, 0.12), color: colors.purple, fontWeight: 600 }} />
                <Chip label={`${loadedPayload.menuCount} menus`} sx={{ bgcolor: alpha(colors.success, 0.12), color: colors.success, fontWeight: 600 }} />
                <Chip label={`${loadedPayload.modifierGroupCount} modifier groups`} sx={{ bgcolor: alpha(colors.textMuted, 0.12), color: colors.textMuted, fontWeight: 600 }} />
                <Chip label={`${loadedPayload.sizeKB} KB`} sx={{ bgcolor: alpha(colors.textMuted, 0.12), color: colors.textMuted, fontWeight: 600 }} />
                {payloadStats && (
                  <>
                    <Divider orientation="vertical" flexItem sx={{ borderColor: colors.border }} />
                    <Chip label={`${payloadStats.suspended} suspended/OOS`} size="small" sx={{ bgcolor: alpha(colors.error, 0.12), color: colors.error, fontSize: '0.7rem' }} />
                    <Chip label={`${payloadStats.lowStock} low stock`} size="small" sx={{ bgcolor: alpha(colors.warning, 0.12), color: colors.warning, fontSize: '0.7rem' }} />
                    <Chip label={`${payloadStats.withPrice} with price`} size="small" sx={{ bgcolor: alpha(colors.success, 0.12), color: colors.success, fontSize: '0.7rem' }} />
                    <Chip label={`${payloadStats.withImage} with image`} size="small" sx={{ bgcolor: alpha(colors.info, 0.12), color: colors.info, fontSize: '0.7rem' }} />
                  </>
                )}
              </Stack>

              {/* Metadata */}
              <Box sx={{ mb: 2, p: 1.5, borderRadius: 1, bgcolor: alpha(colors.bgElevated, 0.5) }}>
                <Typography variant="caption" sx={{ color: colors.textMuted, fontFamily: 'monospace', fontSize: '0.65rem', display: 'block' }}>
                  external_store_id: {loadedPayload.externalStoreId} | event_type: {loadedPayload.eventType}
                  {loadedPayload.timestamp && ` | timestamp: ${loadedPayload.timestamp}`}
                  {loadedPayload.source && ` | source: ${loadedPayload.source}`}
                </Typography>
                <Typography variant="caption" sx={{ color: colors.warning, fontSize: '0.6rem', fontStyle: 'italic', display: 'block', mt: 0.5 }}>
                  Note: external_store_id, event_type, timestamp, source may have been injected by the old Lambda — not part of the original SKC payload
                </Typography>
              </Box>

              {/* Category → Menu mapping */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ color: colors.textMuted, fontWeight: 600, textTransform: 'uppercase', fontSize: '0.65rem' }}>
                  Menu → Category hierarchy ({loadedPayload.menuCount} menus, {loadedPayload.categoryCount} categories)
                </Typography>
                <TableContainer sx={{ maxHeight: 200, mt: 0.5 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ bgcolor: colors.bgElevated, color: colors.textMuted, fontSize: '0.7rem', py: 0.5 }}>Menu</TableCell>
                        <TableCell sx={{ bgcolor: colors.bgElevated, color: colors.textMuted, fontSize: '0.7rem', py: 0.5 }}>Category</TableCell>
                        <TableCell sx={{ bgcolor: colors.bgElevated, color: colors.textMuted, fontSize: '0.7rem', py: 0.5 }} align="right">Items</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(menuPayload.menus || []).map((menu: any) =>
                        (menu.category_ids || []).map((catId: string, ci: number) => {
                          const cat = (menuPayload.categories || []).find((c: any) => c.id === catId);
                          return (
                            <TableRow key={`${menu.id}-${catId}`}>
                              {ci === 0 ? (
                                <TableCell rowSpan={(menu.category_ids || []).length} sx={{ color: colors.textPrimary, fontSize: '0.75rem', verticalAlign: 'top' }}>
                                  {extractText(menu.title) || menu.id}
                                </TableCell>
                              ) : null}
                              <TableCell sx={{ color: colors.textSecondary, fontSize: '0.75rem' }}>
                                {extractText(cat?.title) || catId}
                              </TableCell>
                              <TableCell align="right" sx={{ color: colors.info, fontSize: '0.75rem', fontWeight: 600 }}>
                                {cat?.entities?.length || 0}
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              {/* Item preview */}
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ cursor: 'pointer' }} onClick={() => setShowItemPreview(!showItemPreview)}>
                  <Typography variant="caption" sx={{ color: colors.textMuted, fontWeight: 600, textTransform: 'uppercase', fontSize: '0.65rem' }}>
                    Item preview ({menuPayload.items?.length || 0} items)
                  </Typography>
                  {showItemPreview ? <ExpandLessIcon sx={{ color: colors.textMuted, fontSize: 18 }} /> : <ExpandMoreIcon sx={{ color: colors.textMuted, fontSize: 18 }} />}
                </Stack>
                <Collapse in={showItemPreview}>
                  <TableContainer sx={{ maxHeight: 300, mt: 0.5 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ bgcolor: colors.bgElevated, color: colors.textMuted, fontSize: '0.7rem', py: 0.5 }}>SKU</TableCell>
                          <TableCell sx={{ bgcolor: colors.bgElevated, color: colors.textMuted, fontSize: '0.7rem', py: 0.5 }}>Name</TableCell>
                          <TableCell sx={{ bgcolor: colors.bgElevated, color: colors.textMuted, fontSize: '0.7rem', py: 0.5 }} align="right">Price</TableCell>
                          <TableCell sx={{ bgcolor: colors.bgElevated, color: colors.textMuted, fontSize: '0.7rem', py: 0.5 }} align="center">Stock</TableCell>
                          <TableCell sx={{ bgcolor: colors.bgElevated, color: colors.textMuted, fontSize: '0.7rem', py: 0.5 }} align="center">Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(menuPayload.items || []).map((item: any, idx: number) => {
                          const suspended = isSuspended(item);
                          const stock = extractStock(item);
                          return (
                            <TableRow key={item.id || idx} sx={{ bgcolor: suspended ? alpha(colors.error, 0.03) : 'transparent' }}>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', color: colors.textPrimary, fontSize: '0.7rem' }}>
                                  {item.id}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ color: colors.textPrimary, fontSize: '0.75rem', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {extractText(item.title) || '-'}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" sx={{ color: colors.textSecondary, fontSize: '0.75rem', fontFamily: 'monospace' }}>
                                  {extractPriceDisplay(item)}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Typography variant="body2" sx={{ color: stock <= 1 ? colors.warning : colors.success, fontSize: '0.75rem', fontWeight: 600 }}>
                                  {stock}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                {suspended ? (
                                  <Chip icon={<OutOfStockIcon sx={{ fontSize: 12 }} />} label="OOS" size="small"
                                    sx={{ bgcolor: alpha(colors.error, 0.12), color: colors.error, fontSize: '0.6rem', height: 20 }} />
                                ) : stock <= 1 ? (
                                  <Chip label="Low" size="small"
                                    sx={{ bgcolor: alpha(colors.warning, 0.12), color: colors.warning, fontSize: '0.6rem', height: 20 }} />
                                ) : (
                                  <Chip label="OK" size="small"
                                    sx={{ bgcolor: alpha(colors.success, 0.12), color: colors.success, fontSize: '0.6rem', height: 20 }} />
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Collapse>
              </Box>
            </Paper>
          )}

          {/* Menu Results Log */}
          {menuResults.length > 0 && (
            <Paper sx={{ ...cardStyle }}>
              <Box sx={{ p: 2, borderBottom: `1px solid ${colors.border}`, cursor: 'pointer', '&:hover': { bgcolor: alpha(colors.info, 0.04) } }}
                onClick={() => setShowMenuResults(!showMenuResults)}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <TestIcon sx={{ color: colors.info, fontSize: 20 }} />
                    <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 700 }}>Menu Sync Log ({menuResults.length})</Typography>
                    <Chip label={`${menuResults.filter((r) => r.success).length} OK`} size="small" sx={{ bgcolor: alpha(colors.success, 0.12), color: colors.success, fontSize: '0.6rem', height: 20 }} />
                    {menuResults.some((r) => !r.success) && (
                      <Chip label={`${menuResults.filter((r) => !r.success).length} FAIL`} size="small" sx={{ bgcolor: alpha(colors.error, 0.12), color: colors.error, fontSize: '0.6rem', height: 20 }} />
                    )}
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Button size="small" onClick={(e) => { e.stopPropagation(); setMenuResults([]); }} sx={{ color: colors.textMuted, fontSize: '0.7rem' }}>Clear</Button>
                    {showMenuResults ? <ExpandLessIcon sx={{ color: colors.textMuted }} /> : <ExpandMoreIcon sx={{ color: colors.textMuted }} />}
                  </Stack>
                </Stack>
              </Box>
              <Collapse in={showMenuResults}>
                <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                  {menuResults.map((result, index) => (
                    <Box key={`menu-${result.timestamp}-${index}`} sx={{
                      p: 2, borderBottom: `1px solid ${colors.border}`,
                      bgcolor: result.success ? alpha(colors.success, 0.03) : alpha(colors.error, 0.03),
                    }}>
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                        {result.success ? <SuccessIcon sx={{ color: colors.success, fontSize: 18 }} /> : <ErrorIcon sx={{ color: colors.error, fontSize: 18 }} />}
                        <Chip label="Gateway" size="small" sx={{
                          bgcolor: alpha(colors.success, 0.12),
                          color: colors.success,
                          fontSize: '0.6rem', height: 18,
                        }} />
                        <Typography variant="caption" sx={{ color: colors.textMuted }}>
                          {result.payloadSummary.items} items, {result.payloadSummary.categories} cats, {result.payloadSummary.menus} menus ({result.payloadSummary.sizeKB} KB)
                        </Typography>
                        <Box sx={{ flex: 1 }} />
                        <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '0.65rem' }}>
                          {new Date(result.timestamp).toLocaleTimeString()}
                        </Typography>
                      </Stack>
                      <Typography variant="caption" component="pre" sx={{
                        color: colors.textSecondary, fontFamily: 'monospace', fontSize: '0.65rem',
                        whiteSpace: 'pre-wrap', wordBreak: 'break-all', m: 0,
                        maxHeight: 120, overflow: 'auto',
                        p: 1, borderRadius: 1, bgcolor: alpha(colors.bgElevated, 0.5),
                      }}>
                        {JSON.stringify(result.response, null, 2)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Collapse>
            </Paper>
          )}
        </TabPanel>

        {/* ============================================================ */}
        {/* TAB 1: Item Update */}
        {/* ============================================================ */}
        <TabPanel value={activeTab} index={1}>
          {/* Configuration */}
          <Paper sx={{ ...cardStyle, p: 2.5, mb: 2 }}>
            <Stack spacing={2.5}>
              <Typography variant="subtitle2" sx={{ color: colors.textSecondary, fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Item Webhook via Gateway
              </Typography>
              <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '0.7rem', mt: -1 }}>
                Sends PUT /v2/eats/stores/{'{store_id}'}/menu-items/{'{item_id}'} → SKC Gateway → Lambda → Backend
              </Typography>

              {/* Store selector */}
              <FormControl size="small" sx={{ minWidth: 300, ...elevatedInputStyle }}>
                <InputLabel>Store</InputLabel>
                <Select value={itemStoreId} onChange={(e) => setItemStoreId(e.target.value)} label="Store">
                  {stores.map((store) => (
                    <MuiMenuItem key={store.id} value={store.externalId}>
                      <Stack>
                        <Typography variant="body2">{store.name}</Typography>
                        <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '0.65rem' }}>{store.externalId}</Typography>
                      </Stack>
                    </MuiMenuItem>
                  ))}
                </Select>
              </FormControl>

              <Divider sx={{ borderColor: colors.border }} />

              {/* Product picker */}
              <Box>
                <Typography variant="caption" sx={{ color: colors.textMuted, fontWeight: 600, textTransform: 'uppercase', fontSize: '0.65rem', mb: 1, display: 'block' }}>
                  Select Product (item_id = SKU)
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center">
                  <TextField
                    placeholder="Search products..."
                    size="small"
                    value={itemProductSearch}
                    onChange={(e) => setItemProductSearch(e.target.value)}
                    sx={{ minWidth: 250, ...elevatedInputStyle }}
                  />
                  <TextField
                    label="Item ID override"
                    size="small"
                    value={itemIdOverride}
                    onChange={(e) => setItemIdOverride(e.target.value)}
                    placeholder={selectedProduct?.sku || 'SKU or Uber item ID'}
                    sx={{ minWidth: 200, ...elevatedInputStyle }}
                    InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
                  />
                </Stack>

                {/* Product search results */}
                {itemProductSearch.length >= 2 && (
                  <Box sx={{ mt: 1, maxHeight: 200, overflow: 'auto', borderRadius: 1, border: `1px solid ${colors.border}` }}>
                    {products
                      .filter((p) => p.name.toLowerCase().includes(itemProductSearch.toLowerCase()) || p.sku.toLowerCase().includes(itemProductSearch.toLowerCase()))
                      .slice(0, 10)
                      .map((p) => (
                        <Box
                          key={p.id}
                          onClick={() => { setSelectedProduct(p); setItemIdOverride(''); setItemProductSearch(''); }}
                          sx={{
                            px: 2, py: 1, cursor: 'pointer',
                            bgcolor: selectedProduct?.id === p.id ? alpha(colors.info, 0.08) : 'transparent',
                            '&:hover': { bgcolor: alpha(colors.info, 0.06) },
                            borderBottom: `1px solid ${alpha(colors.border, 0.5)}`,
                          }}
                        >
                          <Stack direction="row" spacing={2} alignItems="center">
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', color: colors.info, fontSize: '0.75rem', minWidth: 100 }}>{p.sku}</Typography>
                            <Typography variant="body2" sx={{ color: colors.textPrimary, fontSize: '0.8rem' }}>{p.name}</Typography>
                            <Typography variant="caption" sx={{ color: colors.textMuted }}>R{p.priceTier1?.toFixed(2)}</Typography>
                          </Stack>
                        </Box>
                      ))}
                  </Box>
                )}

                {selectedProduct && (
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                    <Chip
                      label={`${selectedProduct.sku} — ${selectedProduct.name}`}
                      onDelete={() => setSelectedProduct(null)}
                      sx={{ bgcolor: alpha(colors.info, 0.12), color: colors.info, fontWeight: 600 }}
                    />
                  </Stack>
                )}
              </Box>

              <Divider sx={{ borderColor: colors.border }} />

              {/* Stock + Suspension controls */}
              <Stack direction="row" spacing={4} alignItems="center">
                <Box sx={{ width: 250 }}>
                  <Typography variant="caption" sx={{ color: colors.textMuted, fontWeight: 600, fontSize: '0.65rem' }}>
                    Stock (max_permitted)
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Slider
                      value={itemStock}
                      onChange={(_, val) => setItemStock(val as number)}
                      min={0} max={50} size="small"
                      sx={{ color: itemStock === 0 ? colors.error : colors.success, '& .MuiSlider-thumb': { width: 16, height: 16 } }}
                    />
                    <TextField
                      value={itemStock}
                      onChange={(e) => setItemStock(Math.max(0, parseInt(e.target.value) || 0))}
                      size="small" type="number"
                      inputProps={{ min: 0, max: 999, style: { width: 50, padding: '4px 8px' } }}
                      sx={{ '& .MuiInputBase-root': { bgcolor: colors.bgElevated, color: colors.textPrimary, height: 32 } }}
                    />
                  </Stack>
                </Box>

                <FormControlLabel
                  control={<Switch checked={itemSuspended} onChange={(e) => setItemSuspended(e.target.checked)}
                    sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: colors.error }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: alpha(colors.error, 0.5) } }} />}
                  label={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" sx={{ color: itemSuspended ? colors.error : colors.textSecondary }}>
                        {itemSuspended ? 'Suspended (OOS)' : 'Available'}
                      </Typography>
                      {itemSuspended && <OutOfStockIcon sx={{ color: colors.error, fontSize: 16 }} />}
                    </Stack>
                  }
                />
              </Stack>

              <Divider sx={{ borderColor: colors.border }} />

              {/* Payload preview */}
              <Box>
                <Typography variant="caption" sx={{ color: colors.textMuted, fontWeight: 600, textTransform: 'uppercase', fontSize: '0.65rem', mb: 0.5, display: 'block' }}>
                  Payload Preview — PUT /v2/eats/stores/{itemStoreId || '{store_id}'}/menu-items/{itemIdOverride || selectedProduct?.sku || '{item_id}'}
                </Typography>
                <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: alpha(colors.bgElevated, 0.5), fontFamily: 'monospace', fontSize: '0.7rem', color: colors.textSecondary, whiteSpace: 'pre-wrap', maxHeight: 200, overflow: 'auto' }}>
                  {JSON.stringify(buildItemPayload(), null, 2)}
                </Box>
              </Box>

              {/* Send button */}
              <Stack direction="row" spacing={2} alignItems="center">
              </Stack>
            </Stack>
          </Paper>

          {/* Item Update Results */}
          {itemResults.length > 0 && (
            <Paper sx={{ ...cardStyle }}>
              <Box sx={{ p: 2, borderBottom: `1px solid ${colors.border}`, cursor: 'pointer', '&:hover': { bgcolor: alpha(colors.info, 0.04) } }}
                onClick={() => setShowItemResults(!showItemResults)}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <TestIcon sx={{ color: colors.info, fontSize: 20 }} />
                    <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 700 }}>Item Update Log ({itemResults.length})</Typography>
                    <Chip label={`${itemResults.filter((r) => r.success).length} OK`} size="small" sx={{ bgcolor: alpha(colors.success, 0.12), color: colors.success, fontSize: '0.6rem', height: 20 }} />
                    {itemResults.some((r) => !r.success) && (
                      <Chip label={`${itemResults.filter((r) => !r.success).length} FAIL`} size="small" sx={{ bgcolor: alpha(colors.error, 0.12), color: colors.error, fontSize: '0.6rem', height: 20 }} />
                    )}
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Button size="small" onClick={(e) => { e.stopPropagation(); setItemResults([]); }} sx={{ color: colors.textMuted, fontSize: '0.7rem' }}>Clear</Button>
                    {showItemResults ? <ExpandLessIcon sx={{ color: colors.textMuted }} /> : <ExpandMoreIcon sx={{ color: colors.textMuted }} />}
                  </Stack>
                </Stack>
              </Box>
              <Collapse in={showItemResults}>
                <TableContainer sx={{ maxHeight: 300 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ bgcolor: colors.bgElevated, color: colors.textMuted, width: 40 }} />
                        <TableCell sx={{ bgcolor: colors.bgElevated, color: colors.textMuted }}>Item ID</TableCell>
                        <TableCell sx={{ bgcolor: colors.bgElevated, color: colors.textMuted }}>Store</TableCell>
                        <TableCell sx={{ bgcolor: colors.bgElevated, color: colors.textMuted }}>Stock</TableCell>
                        <TableCell sx={{ bgcolor: colors.bgElevated, color: colors.textMuted }}>Status</TableCell>
                        <TableCell sx={{ bgcolor: colors.bgElevated, color: colors.textMuted }}>Gateway</TableCell>
                        <TableCell sx={{ bgcolor: colors.bgElevated, color: colors.textMuted }}>Time</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {itemResults.map((result, index) => (
                        <TableRow key={`item-${result.timestamp}-${index}`} sx={{ bgcolor: result.success ? alpha(colors.success, 0.03) : alpha(colors.error, 0.03) }}>
                          <TableCell>{result.success ? <SuccessIcon sx={{ color: colors.success, fontSize: 18 }} /> : <ErrorIcon sx={{ color: colors.error, fontSize: 18 }} />}</TableCell>
                          <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace', color: colors.textPrimary, fontSize: '0.75rem' }}>{result.itemId}</Typography></TableCell>
                          <TableCell><Typography variant="caption" sx={{ fontFamily: 'monospace', color: colors.textMuted, fontSize: '0.65rem' }}>{result.storeId.substring(0, 12)}...</Typography></TableCell>
                          <TableCell>
                            <Chip label={result.stock} size="small" sx={{ bgcolor: alpha(result.stock === 0 ? colors.error : colors.success, 0.12), color: result.stock === 0 ? colors.error : colors.success, fontSize: '0.65rem', height: 20 }} />
                          </TableCell>
                          <TableCell>
                            <Chip label={result.suspended ? 'Suspended' : 'Available'} size="small" sx={{
                              bgcolor: alpha(result.suspended ? colors.error : colors.success, 0.12),
                              color: result.suspended ? colors.error : colors.success,
                              fontSize: '0.6rem', height: 20,
                            }} />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" sx={{ color: result.success ? colors.success : colors.error, fontSize: '0.65rem' }}>
                              {result.response?.gateway_status || 'ERR'}
                            </Typography>
                          </TableCell>
                          <TableCell><Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '0.65rem' }}>{new Date(result.timestamp).toLocaleTimeString()}</Typography></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Collapse>
            </Paper>
          )}
        </TabPanel>

        {/* ============================================================ */}
        {/* TAB 2: Stored Payloads */}
        {/* ============================================================ */}
        <TabPanel value={activeTab} index={2}>
          {/* Controls */}
          <Paper sx={{ ...cardStyle, p: 2, mb: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
              <Stack direction="row" spacing={2} alignItems="center">
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel sx={{ color: colors.textMuted }}>Status</InputLabel>
                  <Select
                    value={payloadStatusFilter}
                    label="Status"
                    onChange={(e) => { setPayloadStatusFilter(e.target.value); fetchPayloads(e.target.value || undefined); }}
                    sx={{ ...elevatedInputStyle, fontSize: '0.8rem' }}
                  >
                    <MuiMenuItem value="">All</MuiMenuItem>
                    <MuiMenuItem value="failed">Failed</MuiMenuItem>
                    <MuiMenuItem value="stored">Stored</MuiMenuItem>
                    <MuiMenuItem value="processed">Processed</MuiMenuItem>
                    <MuiMenuItem value="processing">Processing</MuiMenuItem>
                  </Select>
                </FormControl>
                <Chip
                  label={`${payloadTotal} payload${payloadTotal !== 1 ? 's' : ''}`}
                  size="small"
                  sx={{ bgcolor: alpha(colors.info, 0.1), color: colors.info, fontWeight: 600 }}
                />
                {payloadStores.some(s => s.payloads.some(p => p.status === 'failed')) && (
                  <Chip
                    icon={<WarningIcon sx={{ fontSize: 14 }} />}
                    label={`${payloadStores.reduce((acc, s) => acc + s.payloads.filter(p => p.status === 'failed').length, 0)} failed`}
                    size="small"
                    color="error"
                    variant="outlined"
                    sx={{ fontWeight: 600 }}
                  />
                )}
              </Stack>
              <Stack direction="row" spacing={1}>
                {payloadStatusFilter === 'failed' && payloadStores.some(s => s.payloads.some(p => p.status === 'failed')) && (
                  <Button
                    size="small"
                    color="error"
                    variant="outlined"
                    startIcon={<ClearAllIcon sx={{ fontSize: 16 }} />}
                    onClick={() => handleBulkDeletePayloads('failed')}
                    sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                  >
                    Delete All Failed
                  </Button>
                )}
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={payloadLoading ? <CircularProgress size={14} /> : <RefreshIcon sx={{ fontSize: 16 }} />}
                  onClick={() => fetchPayloads(payloadStatusFilter || undefined)}
                  disabled={payloadLoading}
                  sx={{ textTransform: 'none', fontSize: '0.75rem', borderColor: colors.border, color: colors.textMuted }}
                >
                  Refresh
                </Button>
              </Stack>
            </Stack>
          </Paper>

          {/* Payload list grouped by store */}
          {payloadLoading && !payloadStores.length ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <CircularProgress size={32} />
              <Typography variant="body2" sx={{ color: colors.textMuted, mt: 1 }}>Loading payloads...</Typography>
            </Box>
          ) : payloadStores.length === 0 ? (
            <Paper sx={{ ...cardStyle, p: 4, textAlign: 'center' }}>
              <StorageIcon sx={{ fontSize: 48, color: colors.textMuted, mb: 1 }} />
              <Typography variant="body1" sx={{ color: colors.textMuted }}>No payloads found</Typography>
              <Typography variant="caption" sx={{ color: colors.textMuted }}>
                {payloadStatusFilter ? `No ${payloadStatusFilter} payloads. Try a different filter.` : 'Menu webhooks will appear here when received.'}
              </Typography>
            </Paper>
          ) : (
            <Stack spacing={1.5}>
              {payloadStores.map((storeGroup) => {
                const failedCount = storeGroup.payloads.filter(p => p.status === 'failed').length;
                const processedCount = storeGroup.payloads.filter(p => p.status === 'processed').length;
                const storedCount = storeGroup.payloads.filter(p => p.status === 'stored').length;

                return (
                  <Accordion
                    key={storeGroup.externalStoreId}
                    defaultExpanded={failedCount > 0}
                    sx={{
                      bgcolor: colors.bgElevated,
                      border: `1px solid ${failedCount > 0 ? alpha(colors.error, 0.3) : colors.border}`,
                      borderRadius: '8px !important',
                      '&:before': { display: 'none' },
                      '&.Mui-expanded': { margin: '0 !important' },
                    }}
                  >
                    <AccordionSummary expandIcon={<AccordionExpandIcon sx={{ color: colors.textMuted }} />}>
                      <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%', pr: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: colors.textPrimary }}>
                          {storeGroup.storeName || storeGroup.externalStoreId}
                        </Typography>
                        {!storeGroup.storeName && (
                          <Chip label="Unknown store" size="small" variant="outlined" color="warning" sx={{ fontSize: '0.6rem', height: 18 }} />
                        )}
                        <Chip label={`${storeGroup.payloads.length} payload${storeGroup.payloads.length !== 1 ? 's' : ''}`} size="small"
                          sx={{ bgcolor: alpha(colors.info, 0.1), color: colors.info, fontSize: '0.6rem', height: 20 }} />
                        {failedCount > 0 && (
                          <Chip icon={<ErrorIcon sx={{ fontSize: 12 }} />} label={`${failedCount} failed`} size="small" color="error" sx={{ fontSize: '0.6rem', height: 20 }} />
                        )}
                        {processedCount > 0 && (
                          <Chip label={`${processedCount} processed`} size="small" sx={{ bgcolor: alpha(colors.success, 0.1), color: colors.success, fontSize: '0.6rem', height: 20 }} />
                        )}
                        {storedCount > 0 && (
                          <Chip label={`${storedCount} stored`} size="small" sx={{ bgcolor: alpha(colors.warning, 0.1), color: colors.warning, fontSize: '0.6rem', height: 20 }} />
                        )}
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ bgcolor: colors.bgCard, color: colors.textMuted, fontSize: '0.7rem', py: 0.5 }}>Status</TableCell>
                              <TableCell sx={{ bgcolor: colors.bgCard, color: colors.textMuted, fontSize: '0.7rem', py: 0.5 }}>Items</TableCell>
                              <TableCell sx={{ bgcolor: colors.bgCard, color: colors.textMuted, fontSize: '0.7rem', py: 0.5 }}>Categories</TableCell>
                              <TableCell sx={{ bgcolor: colors.bgCard, color: colors.textMuted, fontSize: '0.7rem', py: 0.5 }}>Size</TableCell>
                              <TableCell sx={{ bgcolor: colors.bgCard, color: colors.textMuted, fontSize: '0.7rem', py: 0.5 }}>Received</TableCell>
                              <TableCell sx={{ bgcolor: colors.bgCard, color: colors.textMuted, fontSize: '0.7rem', py: 0.5 }}>Error</TableCell>
                              <TableCell sx={{ bgcolor: colors.bgCard, color: colors.textMuted, fontSize: '0.7rem', py: 0.5, width: 120 }}>Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {storeGroup.payloads.map((payload) => (
                              <TableRow
                                key={payload.id}
                                sx={{
                                  bgcolor: payload.status === 'failed' ? alpha(colors.error, 0.04) : 'transparent',
                                  '&:hover': { bgcolor: alpha(colors.info, 0.04) },
                                }}
                              >
                                <TableCell>
                                  <Chip
                                    size="small"
                                    icon={
                                      payload.status === 'failed' ? <ErrorIcon sx={{ fontSize: 12 }} /> :
                                      payload.status === 'processed' ? <ProcessedIcon sx={{ fontSize: 12 }} /> :
                                      payload.status === 'processing' ? <CircularProgress size={10} /> :
                                      <PendingIcon sx={{ fontSize: 12 }} />
                                    }
                                    label={payload.status}
                                    color={payload.status === 'failed' ? 'error' : payload.status === 'processed' ? 'success' : 'default'}
                                    variant={payload.status === 'failed' ? 'filled' : 'outlined'}
                                    sx={{ fontSize: '0.6rem', height: 20 }}
                                  />
                                </TableCell>
                                <TableCell><Typography variant="caption" sx={{ color: colors.textPrimary }}>{payload.itemCount?.toLocaleString()}</Typography></TableCell>
                                <TableCell><Typography variant="caption" sx={{ color: colors.textPrimary }}>{payload.categoryCount}</Typography></TableCell>
                                <TableCell><Typography variant="caption" sx={{ color: colors.textMuted }}>{payload.sizeKB} KB</Typography></TableCell>
                                <TableCell>
                                  <Tooltip title={new Date(payload.createdAt).toLocaleString()}>
                                    <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '0.65rem' }}>
                                      {formatTimeAgo(payload.createdAt)}
                                    </Typography>
                                  </Tooltip>
                                </TableCell>
                                <TableCell>
                                  {payload.errorMessage ? (
                                    <Tooltip title={payload.errorMessage}>
                                      <Typography variant="caption" sx={{ color: colors.error, fontSize: '0.65rem', maxWidth: 200, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {payload.errorMessage}
                                      </Typography>
                                    </Tooltip>
                                  ) : (
                                    <Typography variant="caption" sx={{ color: colors.textMuted }}>-</Typography>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Stack direction="row" spacing={0.5}>
                                    <Tooltip title="Rerun processing">
                                      <span>
                                        <IconButton
                                          size="small"
                                          onClick={() => handleRerunPayload(payload.id)}
                                          disabled={rerunningId === payload.id}
                                          sx={{ color: colors.info }}
                                        >
                                          {rerunningId === payload.id ? <CircularProgress size={14} /> : <ReplayIcon sx={{ fontSize: 16 }} />}
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                    <Tooltip title="Delete payload">
                                      <span>
                                        <IconButton
                                          size="small"
                                          onClick={() => handleDeletePayload(payload.id)}
                                          disabled={deletingId === payload.id}
                                          sx={{ color: colors.error }}
                                        >
                                          {deletingId === payload.id ? <CircularProgress size={14} /> : <DeleteIcon sx={{ fontSize: 16 }} />}
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  </Stack>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Stack>
          )}
        </TabPanel>
      </Box>
    </Box>
  );
}
